import React from "react";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { useForm } from "@/src/provider/Form/FormProvider";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import Address from "@/src/components/Form/Common/Address/Address";
import Group from "@/src/components/Wizard/Group";
import { translate } from "@/src/services/translations/translations";
import IconPerson from "@/src/assets/icons/provision/toggleable/IconPerson";
import IconCompany from "@/src/assets/icons/contract/toggleable/IconCompany";
import Person from "@/src/components/Form/Common/Person/Person";
import TextFieldConfigured from "@/src/components/Form/ConfiguredFormElements/TextFieldConfigured/TextFieldConfigured";
import ValidatorOrganizationAutocomplete from "@/src/components/FormFields/ValidatorOrganizationAutocomplete";
import IconQuestionmark from "@/src/assets/icons/common/toggleable/IconQuestionmark";

type OpposingPartyProps = {
  submitProps: FormSubmitProps;
};

export default function OpposingParty({ submitProps }: OpposingPartyProps) {
  const { handleSubmit, values } = useForm();

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Grid
            container
            sx={{
              justifyContent: "center",
              alignItems: "center"
            }}>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>
                <strong>Angaben zum Unfallgegner</strong>
              </FormLegend>
            </Grid>
            <TextFieldConfigured caseData={values} fieldPath={"licensePlateOpposingParty"} required />
            <Grid size={12}>
              <Group
                type="radio"
                name="opposingPartyType"
                question={translate("trafficAccident.opposingPartyType.question", values)}
                options={[
                  {
                    labelText: translate("trafficAccident.opposingPartyType.options.opposingPartyPerson.label", values),
                    labelIcon: <IconPerson />,
                    labelIconActive: <IconPerson active />,
                    value: "opposingPartyPerson",
                  },
                  {
                    labelText: translate("trafficAccident.opposingPartyType.options.opposingPartyLegalEntity.label", values),
                    labelIcon: <IconCompany />,
                    labelIconActive: <IconCompany active />,
                    value: "opposingPartyLegalEntity",
                  },
                  {
                    labelText: translate("trafficAccident.opposingPartyType.options.null.label", values),
                    labelIcon: <IconQuestionmark />,
                    labelIconActive: <IconQuestionmark active />,
                    value: "null",
                  },
                ]}
              />
            </Grid>
            {values.opposingPartyType === "opposingPartyPerson" && (
              <>
                <Person path="opposingPartyPerson" headline={"Wie heißt der Unfallgegner?"} />
                <Address
                  path="opposingPartyPerson.residenceAddress"
                  headline={
                    "Bitte geben Sie möglichst alle weiteren Daten zum Unfallgegner an, die Ihnen zum jetzigen Zeitpunkt vorliegen. Wenn Ihnen Daten (noch) nicht vorliegen, lassen Sie die Felder gerne einfach leer."
                  }
                  fieldsAreRequired={false}
                />
              </>
            )}
            {values.opposingPartyType === "opposingPartyLegalEntity" && (
              <>
                <TextFieldConfigured caseData={values} fieldPath={"opposingPartyLegalEntity.organization.legalName"} required />
                <Address
                  path="opposingPartyLegalEntity.organization.address"
                  headline={
                    "Bitte geben Sie möglichst alle weiteren Daten zum Unfallgegner an, die Ihnen zum jetzigen Zeitpunkt vorliegen. Wenn Ihnen Daten (noch) nicht vorliegen, lassen Sie die Felder gerne einfach leer."
                  }
                  fieldsAreRequired={false}
                />
              </>
            )}
            {values.opposingPartyType !== "null" && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{translate("trafficAccident.opposingCarLiabilityInsurance.question", values)}</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorOrganizationAutocomplete
                    label={translate("trafficAccident.opposingCarLiabilityInsurance.label", values)}
                    name="opposingCarLiabilityInsurance"
                    type={"opposingCarLiabilityInsurance"}
                  />
                </Grid>

                <TextFieldConfigured caseData={values} fieldPath={"opposingInsurancePolicyNumber"} />
                <TextFieldConfigured caseData={values} fieldPath={"opposingCaseReferenceNumber"} />
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
